'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Download } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Skeleton,
} from '@/components/ui';
import type { components } from '@/lib/api/generated/schema';

type Invoice = components['schemas']['Invoice'];

type InvoiceValue = string | number | undefined;

const columns: ColumnDef<Invoice, InvoiceValue>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: ({ row }) => {
      const timestamp = row.getValue<number>('createdAt');
      if (typeof timestamp !== 'number' || timestamp <= 0) {
        return 'Invalid date';
      }
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString();
    },
  },
  {
    accessorKey: 'amountPaidCents',
    header: 'Amount',
    cell: ({ row }) => {
      const amount = row.getValue<number>('amountPaidCents');
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount / 100);
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue<string>('status');
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
  },
  {
    id: 'actions',
    header: () => <div className="text-center">Download</div>,
    cell: ({ row }) => {
      const invoice = row.original;
      if (!invoice.pdfUrl) {
        return (
          <div className="text-muted-foreground flex items-center justify-center">
            —
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="min-w-28 justify-center"
          >
            <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4" aria-hidden="true" />
              Download
            </a>
          </Button>
        </div>
      );
    },
  },
];

type InvoicesTableProps = {
  invoices: Invoice[];
  isLoading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
};

export function InvoicesTable({
  invoices,
  isLoading,
  hasMore = false,
  onLoadMore,
  isFetchingMore = false,
}: InvoicesTableProps) {
  return (
    <Card>
      <CardHeader>
        {isLoading ? (
          <Skeleton className="h-6 w-28" />
        ) : (
          <CardTitle>Invoices</CardTitle>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <DataTable<Invoice, InvoiceValue>
          columns={columns}
          data={invoices}
          isLoading={isLoading}
          emptyMessage="No invoices yet."
        />
        {hasMore && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={isFetchingMore}
            >
              {isFetchingMore ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
